
export interface FormSubmissionData {
  [key: string]: string;
}

export interface FormSubmissionResult {
  formData: FormSubmissionData;
  formType: string;
}

export interface StandardizedFormSubmission {
  message?: string;
  interestData?: any;
  [key: string]: any;
}

export const createFormSubmission = (
  formData: { [key: string]: string },
  formType: string
): FormSubmissionResult => {
  // Filter out empty values and message field
  const cleanedData: FormSubmissionData = {};
  
  Object.keys(formData).forEach(key => {
    if (key !== 'message' && formData[key] && formData[key].trim().length > 0) {
      cleanedData[key] = formData[key].trim();
    }
  });

  return {
    formData: cleanedData,
    formType: formType.toLowerCase()
  };
};

export const formatFormSubmissionForChat = (
  submission: FormSubmissionResult
): string => {
  // Return a simple confirmation message that will trigger the form_submission renderer
  return `FORM_SUBMISSION:${JSON.stringify({
    type: 'form_submission',
    formData: submission.formData,
    formType: submission.formType
  })}`;
};

export const formatFormSubmissionForOpenAI = (
  submission: FormSubmissionResult,
  interestData?: any
): string => {
  let message = `Customer submitted a ${submission.formType} form with the following information:\n\n`;
  
  // Add form fields
  Object.entries(submission.formData).forEach(([key, value]) => {
    const fieldName = key.charAt(0).toUpperCase() + key.slice(1);
    message += `${fieldName}: ${value}\n`;
  });
  
  // Add interest data if available
  if (interestData && interestData.vehicle) {
    message += `\nVehicle of Interest:\n`;
    const vehicle = interestData.vehicle;
    if (vehicle.make) message += `Make: ${vehicle.make}\n`;
    if (vehicle.model) message += `Model: ${vehicle.model}\n`;
    if (vehicle.version) message += `Trim: ${vehicle.version}\n`;
    if (vehicle.year) message += `Year: ${vehicle.year}\n`;
    if (vehicle.color) message += `Color: ${vehicle.color}\n`;
    if (vehicle.vin) message += `VIN: ${vehicle.vin}\n`;
  }
  
  // Customize ending message based on form type
  if (submission.formType.toLowerCase() === 'offer') {
    message += `\nPlease acknowledge receipt of this form offer submission and mention that a specialist will get back to them with the details of the offer on the vehicle of interest.`;
  } else if (submission.formType.toLowerCase() === 'financev1') {
    message += `\nPlease acknowledge receipt of this credit application and conclude that a credit specialist will contact them shortly.`;
  } else if (submission.formType.toLowerCase() === 'contact') {
    message += `\nPlease acknowledge receipt of this form submission and tell them we'll get back to them shortly.`;
  } else if (submission.formType.toLowerCase() === 'appointment') {
    message += `\nPlease acknowledge receipt of this form Appointment request and tell them we're looking forward to their visit.`;
  } else if (submission.formType.toLowerCase() === 'service') {
    message += `\nPlease acknowledge receipt of this service appointment request and tell them we're looking forward to their visit.`;
  } else if (submission.formType.toLowerCase() === 'carfax') {
    // Enhanced handling for Carfax forms to prioritize VIN from form data or interest data
    const vinFromForm = submission.formData.vin;
    const vinFromInterest = interestData?.vehicle?.vin;
    const finalVin = vinFromForm || vinFromInterest;
    
    if (finalVin) {
      message += `\nPlease acknowledge receipt of this Carfax request and provide the CARFAX_URL for VIN: ${finalVin}. The customer is requesting a Carfax report for this specific vehicle.`;
    } else {
      message += `\nPlease acknowledge receipt of this Carfax request and provide the CARFAX_URL. The customer is requesting a Carfax report.`;
    }
  } else {
    message += `\nPlease acknowledge receipt of this form submission and respond with the CARFAX_URL.`;
  }
  
  return message;
};

// NEW: Centralized form submission handler
export const createStandardizedFormSubmission = (
  formData: { [key: string]: string },
  formType: string,
  formatPhoneNumber: (phone: string) => string,
  interestData?: any
): StandardizedFormSubmission => {
  // Create submission data with formatted phone
  const submissionData = {
    ...formData,
    phone: formData.phone ? formatPhoneNumber(formData.phone) : ''
  };

  // Create form submission using existing utilities
  const submission = createFormSubmission(submissionData, formType);
  const chatMessage = formatFormSubmissionForChat(submission);

  // Return standardized submission object with clean form display
  return {
    message: chatMessage,
    ...submission.formData,
    interestData: interestData || {}
  };
};
